import { copyToClipboard, Dialog, Notify } from 'quasar'
import { useAuthStore } from 'src/entities/auth'
import {
  buildDiagnosticsReport,
  readBuildInfo,
  type DiagnosticsServerFacts,
} from 'src/shared/lib/build-info'

/** Отчёт показываем внутри `<pre>` — экранируем, чтобы UA с `<` не съел разметку. */
const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

/**
 * Сбор диагностики «для отчёта об ошибке» в буфер обмена.
 *
 * Одна точка на два потребителя: плашка версии (`BuildVersionTag`, доступна
 * везде и всем ролям) и кнопка на странице «Подключение и система» — та
 * добавляет серверные факты из `q.system.info`. Дублировать сбор нельзя: две
 * копии разъедутся, и присланный отчёт будет зависеть от того, откуда его сняли.
 *
 * Всё, что зависит от браузера (`location`, `navigator`, текущее время),
 * читается ЗДЕСЬ и в момент клика — чистое форматирование живёт в `build-info.ts`
 * и покрыто тестами.
 */
export const useDiagnosticsReport = () => {
  const auth = useAuthStore()

  /**
   * Кладёт отчёт в буфер. `server` — серверные факты, если они на руках
   * (их отдаёт только `q.system.info` администратору установки).
   */
  const copyDiagnostics = async (server?: DiagnosticsServerFacts | null): Promise<void> => {
    const report = buildDiagnosticsReport({
      build: readBuildInfo(),
      tenantId: auth.tenantId,
      roles: auth.roles,
      username: auth.username,
      url: window.location.href,
      userAgent: navigator.userAgent,
      now: new Date(),
      server: server ?? null,
    })

    try {
      await copyToClipboard(report)
      Notify.create({
        type: 'positive',
        message: 'Данные для отчёта скопированы',
        caption: 'Вставьте их в описание проблемы',
        timeout: 2500,
      })
    } catch {
      // copyToClipboard reject'ится без clipboard-permission и в insecure context
      // (админка по http, а не https). Показать «скопировано» при пустом буфере
      // нельзя — человек отправит отчёт без данных и не узнает об этом, поэтому
      // отдаём текст диалогом: выделить и скопировать руками.
      Notify.create({
        type: 'negative',
        message: 'Не удалось скопировать в буфер — скопируйте текст вручную',
      })
      Dialog.create({
        title: 'Данные для отчёта об ошибке',
        message: `<pre style="white-space:pre-wrap;font-size:12px;margin:0">${escapeHtml(report)}</pre>`,
        html: true,
        ok: 'Закрыть',
      })
    }
  }

  return { copyDiagnostics }
}
